import { memo, useMemo } from "react";
import { AgingReport, Month, toPrettyCurrency } from "common";
import { BalanceCell } from "./BalanceCell";
import {
  AgingReportBalanceStatusType,
  AgingReportWithStatusesByMonth,
} from "../util/getAgingReportStatus";
import { getReportAlerts, ReportAlertContext } from "../util/alerts";

// Memoized and free of router/store hooks: the dashboard renders hundreds of these
export const ReportRow = memo(function ReportRow({
  report,
  months,
  statusFilters,
  setActiveReport,
  openChat,
  alertContext,
}: {
  report: AgingReportWithStatusesByMonth;
  months: Month[];
  statusFilters: AgingReportBalanceStatusType[];
  setActiveReport: (report: AgingReport) => void;
  /** Opens the resident's conversation in a docked chat window */
  openChat?: (report: AgingReport) => void;
  /** Must keep its identity between renders, or every row re-renders */
  alertContext?: ReportAlertContext;
}) {
  const alerts = useMemo(() => (alertContext ? getReportAlerts(report, alertContext) : []), [report, alertContext]);
  const overdue = alerts.find((alert) => alert.kind === "overdue");
  const messages = alerts.filter((alert) => alert.kind === "message").length;

  return (
    <tr onClick={() => setActiveReport(report)} className="cursor">
      <td>
          {report.residentName}
          {(overdue || messages > 0) && (
            <span className="row-alerts">
              {overdue && (
                <span className="row-alert is-overdue" title={`Follow-up overdue since ${overdue.detail}`}>
                  <i className="fa-solid fa-clock" aria-label="Follow-up overdue" />
                </span>
              )}
              {messages > 0 && (
                <button
                  type="button"
                  className="row-alert is-message"
                  title={openChat ? `${messages} messages. Open chat window` : `${messages} messages`}
                  aria-label={`${messages} messages for ${report.residentName}. Open chat window`}
                  onClick={(event) => {
                    if (!openChat) return;
                    event.stopPropagation();
                    openChat(report);
                  }}
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  {messages}
                </button>
              )}
            </span>
          )}
          {openChat && messages === 0 && (
            <button
              type="button"
              className="row-chat"
              title="Open chat window"
              aria-label={`Open chat window for ${report.residentName}`}
              onClick={(event) => {
                event.stopPropagation();
                openChat(report);
              }}
            >
              <i className="fa-regular fa-comment-dots" aria-hidden="true" />
            </button>
          )}
      </td>
      <td>{report.billType}</td>
      {months?.map((month) => {
        return (
          <BalanceCell
            statusFilters={statusFilters}
            key={`${report.sk}-${month}`}
            balance={report?.balances?.[month]}
            statusesMap={report?.statusesByMonth?.[month]}
          />
        );
      })}
      <td>{toPrettyCurrency(report.prior)}</td>
      <td>{toPrettyCurrency(report.net)}</td>
      <td>{report.medicaidApplication}</td>
      <td>{report.agingStatus}</td>
    </tr>
  );
});
